import { useEffect, useState } from 'react';
import axios from 'axios';

import { CreditRisk } from '@types';
import { moneyFormatter } from '@utils';
import { ScoreWidget } from '@components/ScoreWidget';
import cls from './GeneralInfo.module.css';
import { clsx } from 'clsx';


interface Props {
  risk: CreditRisk;
}

const CompanyIcon = () => (
  <svg className={cls.companyIcon} width="1em" height="1em" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M8.5 0C13.1944 0 17 3.80558 17 8.5C17 13.1944 13.1944 17 8.5 17C3.80558 17 0 13.1944 0 8.5C0 3.80558 3.80558 0 8.5 0ZM3.40039 3.40039V13.4775H5.51562V11.6816C5.51562 11.351 5.78449 11.083 6.11523 11.083C6.44579 11.0832 6.71387 11.3511 6.71387 11.6816V13.4775H10.9658V11.6816C10.9658 11.351 11.2337 11.083 11.5645 11.083C11.8952 11.083 12.1631 11.351 12.1631 11.6816V13.4775H14.2988V6.79199H8.84961V3.40039H3.40039ZM5.51562 8.9873V9.98535H4.51758V8.9873H5.51562ZM7.71191 8.9873V9.98535H6.71387V8.9873H7.71191ZM10.9658 8.9873V9.98535H9.96777V8.9873H10.9658ZM13.1611 8.9873V9.98535H12.1631V8.9873H13.1611ZM5.51562 7.39062V8.38867H4.51758V7.39062H5.51562ZM7.71191 7.39062V8.38867H6.71387V7.39062H7.71191ZM10.9658 7.39062V8.38867H9.96777V7.39062H10.9658ZM13.1611 7.39062V8.38867H12.1631V7.39062H13.1611ZM5.51562 5.79492V6.79199H4.51758V5.79492H5.51562ZM7.71191 5.79492V6.79199H6.71387V5.79492H7.71191ZM5.51562 4.19824V5.19629H4.51758V4.19824H5.51562ZM7.71191 4.19824V5.19629H6.71387V4.19824H7.71191Z" fill="#042E6B"/>
  </svg>
);

export const GeneralInfo = ({ risk }: Props) => {
  const [avatar, setAvatar] = useState<string>();
  const credit = risk.credit;
  const user = credit?.user;
  const score = risk?.riskScore || 0;

  const getTerms = (): string => {
    const terms = risk.creditTerm;
    if (!terms) return '0 Meses';
    if (terms.includes('Meses')) {
      return terms;
    }
    return `${terms} Meses`;
  };

  useEffect(() => {
    user && user.avatar && (async () => {
      const avatarBase64 = await getBase64(user.avatar as string);
      setAvatar(avatarBase64);
    })();
  }, [user]);

  return (
    <div className={clsx(cls.row, 'general-info-wg')}>
      <div className={cls.col}>
        <div className={cls.info}>
          {user?.isCompany ? <div className={cls.company}>
            Company <CompanyIcon />
          </div> : null}
          <div className={cls.line}>
            <div className={cls.label}>ID Verqor</div>
            <div className={cls.value}>{credit?.code}</div>
          </div>
          <div className={cls.line}>
            <div className={cls.label}>Client</div>
            <div className={cls.value}>{credit?.accreditedName}</div>
          </div>
          <div className={cls.line}>
            <div className={cls.label}>RFC</div>
            <div className={cls.value}>{user.rfc || user.rfcActual || '-'}</div>
          </div>
          <div className={cls.line}>
            <div className={cls.label}>Authorized Withholding Company</div>
            <div className={cls.value}>{risk.buyer}</div>
          </div>
          <div className={cls.line}>
            <div className={cls.label}>State</div>
            <div className={cls.value}>{user?.state?.name}</div>
          </div>
          <div className={cls.line}>
            <div className={cls.label}>Crop</div>
            <div className={cls.value}>{credit.creditCrops.map(crop => crop.financedCrop?.nameMx).join(', ')}</div>
          </div>
          <div className={cls.line}>
            <div className={cls.label}>Variety</div>
            <div className={cls.value}>{risk.variety}</div>
          </div>
          <div className={cls.line}>
            <div className={cls.label}>Interest Rate</div>
            <div className={cls.value}>
              {risk.interestRate ? (risk.interestRate / 1.16).toFixed(2) : 0}%
            </div>
          </div>
          <div className={cls.line}>
            <div className={cls.label}>Moratory Rate</div>
            <div className={cls.value}>
              {risk.moratoryRate ? (risk.moratoryRate / 1.16).toFixed(2) : 0}%
            </div>
          </div>
          <div className={cls.line}>
            <div className={cls.label}>Verqor Membership</div>
            <div className={cls.value}>{risk.membership || 0}%</div>
          </div>
          <div className={cls.line}>
            <div className={cls.label}>Credit Term</div>
            <div className={cls.value}>{getTerms()}</div>
          </div>
        </div>
      </div>
      <div className={cls.col}>
        <div className={cls.score}>
          <div className={cls.scoreTitle}>Total Score</div>
          <ScoreWidget
            value={score}
            percents={score > risk.successPercent ? 90 : 10}
            avatarUrl={avatar || undefined}
            deniedText={' '}
            approvedText={' '}
            className={cls.scoreWidget}
          />
          <div className={cls.scoreMoney}>
            <div className={cls.scoreMoneyLabel}>Approved Amount</div>
            <div className={cls.scoreMoneyValue}>
              {moneyFormatter.format(risk.approvedAmount || 0)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const getBase64 = (url: string): Promise<string> => axios
  .get(url, { responseType: 'arraybuffer' })
  .then(response => {
    // @ts-ignore
    const b64encoded = btoa([].reduce.call(
      new Uint8Array(response.data),
      (p: any, c: any) => p + String.fromCharCode(c),
      '',
    ));
    const mimetype = 'image/jpeg';
    return `data:${mimetype};base64,${b64encoded}`;
  });
